// Motion layer (presentation-2 contract section 8): paces displayed observations, captures element
// rectangles before each change, diffs the tables and plays the plan with FLIP through the Web
// Animations API. Everything it animates comes from the seat's own observations.

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SessionState, LogEntry } from '../session/Session';
import type { Description, Observation } from '../protocol/ui';
import { buildTable } from '../model/table';
import { diff, type MotionEvent } from './diff';
import { boundMotion, enqueueObservation, plan, type Plan, type Speed } from './planner';
import { FLOAT_H, FLOAT_W, deltaPlace } from './deltaPlace';
import { BOARD_MOTION, type DisplayedMotion } from '../boards/runtime/motion';
import { clearActivity, publishActivity } from '../display/activity';

export interface Shown { observation: Observation; log: LogEntry[]; revision: number }

export interface MotionFlags { claiming: Map<string, number> }
const MotionFlagsContext = createContext<MotionFlags>({ claiming: new Map() });
export const MotionFlagsProvider = MotionFlagsContext.Provider;
export const useMotionFlags = () => useContext(MotionFlagsContext);

interface Captured { ids: Map<string, { rect: DOMRect; node: Element }>; zones: Map<string, DOMRect>; counters: Map<string, DOMRect>; seats: Map<string, DOMRect> }

function capture(root: HTMLElement | null): Captured {
  const out: Captured = { ids: new Map(), zones: new Map(), counters: new Map(), seats: new Map() };
  if (!root) return out;
  for (const el of root.querySelectorAll('[data-motion-id]')) out.ids.set(el.getAttribute('data-motion-id')!, { rect: el.getBoundingClientRect(), node: el });
  for (const el of root.querySelectorAll('[data-motion-zone]')) out.zones.set(el.getAttribute('data-motion-zone')!, el.getBoundingClientRect());
  for (const el of root.querySelectorAll('[data-motion-counter]')) out.counters.set(el.getAttribute('data-motion-counter')!, el.getBoundingClientRect());
  for (const el of root.querySelectorAll('[data-motion-seat]')) out.seats.set(el.getAttribute('data-motion-seat')!, el.getBoundingClientRect());
  return out;
}

const find = (root: HTMLElement, attr: string, value: string) => root.querySelector(`[${attr}="${CSS.escape(value)}"]`);
const center = (r: DOMRect) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
const visible = (r: DOMRect | undefined) => !!r && r.width > 0 && r.height > 0;

function rectOf(root: HTMLElement, before: Captured, place: { zone: string } | { seat: string } | null): DOMRect | undefined {
  if (!place) return undefined;
  if ('zone' in place) return (find(root, 'data-motion-zone', place.zone)?.getBoundingClientRect()) ?? before.zones.get(place.zone);
  return (find(root, 'data-motion-seat', place.seat)?.getBoundingClientRect()) ?? before.seats.get(place.seat);
}

function ghost(layer: HTMLElement, rect: DOMRect, node?: Element): HTMLElement {
  const el = node ? (node.cloneNode(true) as HTMLElement) : document.createElement('div');
  if (!node) el.className = 'motion-card';
  el.removeAttribute('data-anchor'); el.removeAttribute('data-motion-id');
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, { position: 'fixed', left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, margin: '0', pointerEvents: 'none', zIndex: '60' });
  layer.appendChild(el);
  return el;
}


/** Plays a plan; returns the IDs whose owner fill the route map should animate, with durations. */
function execute(p: Plan, root: HTMLElement, layer: HTMLElement, before: Captured): Map<string, number> {
  const claiming = new Map<string, number>();
  const lanes = new Map<Element, number>();
  if (p.mode === 'fade') { root.animate([{ opacity: 0.6 }, { opacity: 1 }], { duration: p.total, easing: 'linear' }); return claiming; }
  const easing = 'cubic-bezier(.2,.8,.2,1)';
  for (const { event: e, delay, duration, units } of p.steps) {
    if ('id' in e && root.querySelector(`.pixi-board [data-anchor="obj:${CSS.escape(e.id)}"]`)) continue;
    switch (e.type) {
      case 'move': {
        const el = find(root, 'data-motion-id', e.id) as HTMLElement | null;
        const old = before.ids.get(e.id)?.rect;
        if (!el || !visible(old) || el instanceof SVGElement) break;
        const now = el.getBoundingClientRect();
        if (!visible(now)) break;
        const a = center(old!), b = center(now);
        const sx = old!.width / now.width, sy = old!.height / now.height;
        if (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) < 2 && Math.abs(sx - 1) < 0.02) break;
        el.animate([
          { transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${sx}, ${sy})` },
          { transform: `translate(${(a.x - b.x) * 0.45}px, ${(a.y - b.y) * 0.45 - 18}px) scale(${(sx + 1) / 2}, ${(sy + 1) / 2})`, offset: 0.5 },
          { transform: 'none' },
        ], { duration, delay, easing, fill: 'backwards' });
        break;
      }
      case 'enter': {
        const el = find(root, 'data-motion-id', e.id) as HTMLElement | null;
        if (!el || el instanceof SVGElement) break;
        const now = el.getBoundingClientRect();
        const src = rectOf(root, before, e.source);
        if (!visible(now)) break;
        if (!visible(src)) { el.animate([{ opacity: 0, transform: 'scale(0.9)' }, { opacity: 1, transform: 'none' }], { duration, delay, easing, fill: 'backwards' }); break; }
        const a = center(src!), b = center(now);
        el.animate([
          { transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(0.6)`, opacity: 0.4 },
          { transform: 'none', opacity: 1 },
        ], { duration, delay, easing, fill: 'backwards' });
        break;
      }
      case 'exit': {
        const old = before.ids.get(e.id);
        if (!old || !visible(old.rect) || old.node instanceof SVGElement) break;
        const dest = rectOf(root, before, e.dest);
        const g = ghost(layer, old.rect, old.node);
        const a = center(old.rect), b = visible(dest) ? center(dest!) : a;
        const anim = g.animate([
          { transform: 'none', opacity: 1 },
          { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(0.55)`, opacity: visible(dest) ? 0.85 : 0 },
        ], { duration, delay, easing, fill: 'both' });
        anim.onfinish = () => g.remove();
        break;
      }
      case 'count': {
        const from = e.from ? rectOf(root, before, { zone: e.from }) : undefined;
        const to = e.to ? rectOf(root, before, { zone: e.to }) : undefined;
        if (!visible(from) && !visible(to)) break;
        const start = visible(from) ? from! : to!, end = visible(to) ? to! : from!;
        const size = new DOMRect(0, 0, 34, 48);
        for (let i = 0; i < (units ?? 1); i++) {
          const a = center(start), b = center(end);
          const g = ghost(layer, new DOMRect(a.x - size.width / 2, a.y - size.height / 2, size.width, size.height));
          const anim = g.animate([
            { transform: 'none', opacity: visible(from) ? 1 : 0 },
            { transform: `translate(${(b.x - a.x) / 2}px, ${(b.y - a.y) / 2 - 24}px) rotate(-6deg)`, opacity: 1, offset: 0.5 },
            { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px)`, opacity: visible(to) ? 0.9 : 0 },
          ], { duration: Math.max(200, duration - (units! - 1) * 60), delay: delay + i * 60, easing, fill: 'both' });
          anim.onfinish = () => g.remove();
        }
        break;
      }
      case 'counter': {
        const el = find(root, 'data-motion-counter', e.id) as HTMLElement | null;
        if (!el) break;
        el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.16)', offset: 0.3 }, { transform: 'scale(1)' }], { duration, delay, easing: 'ease-out' });
        if (e.delta !== null && e.delta !== 0) {
          const r = el.getBoundingClientRect();
          if (!visible(r)) break;
          const place = deltaPlace(el, r, lanes, window.innerWidth);
          const g = ghost(layer, new DOMRect(place.left, place.top, FLOAT_W, FLOAT_H));
          g.className = `motion-float${e.delta > 0 ? ' is-gain' : ' is-loss'}`;
          const icon = el.querySelector('.strip-icon');
          if (icon && place.inHead) g.appendChild(icon.cloneNode(true));
          g.appendChild(document.createTextNode(e.delta > 0 ? `+${e.delta}` : String(e.delta)));
          const anim = g.animate([{ transform: `translateY(${place.rise}px)`, opacity: 0 }, { opacity: 1, offset: 0.2 }, { opacity: 1, offset: 0.7 }, { transform: 'translateY(0)', opacity: 0 }], { duration: duration * 1.4, delay, easing: 'ease-out', fill: 'both' });
          anim.onfinish = () => g.remove();
        }
        break;
      }
      case 'owner':
        if (e.to) claiming.set(e.id, duration);
        break;
      case 'appearance':
        break;
      case 'turn':
        break;
    }
  }
  return claiming;
}

function reducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export interface DirectorOptions {
  maximumMotionMs?:number;
  self: string | null;
  description: Description | null;
  speed: Speed;
  /** Opponent pace: 0.5..2, or 0 for instant. */
  pace: number;
  segments: (id: string) => number;
  seatColor: (seat: string | null) => string;
  /** Move replay is showing a past position: hold live positions in the queue until it ends. */
  paused?: boolean;
  /** Rewrites a log line for display; null drops it. */
  formatLog?: (message: string) => string | null;
}

export interface Director { shown: Shown | null; flags: MotionFlags; banner: { text: string; color: string; key: number } | null }

/** Paces observations for display and animates each change. Own changes play at once; opponents queue. */
export function useDirector(state: SessionState, root: React.RefObject<HTMLElement | null>, layer: React.RefObject<HTMLElement | null>, options: DirectorOptions): Director {
  const [shown, setShown] = useState<Shown | null>(null);
  const [flags, setFlags] = useState<MotionFlags>({ claiming: new Map() });
  const [banner, setBanner] = useState<Director['banner']>(null);
  const shownRef = useRef<Shown | null>(null);
  const queue = useRef<Shown[]>([]);
  const busy = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ before: Captured; prev: Shown; skipped: boolean } | null>(null);
  const opts = useRef(options);
  opts.current = options;

  const pump = useCallback(() => {
    if (busy.current || opts.current.paused) return;
    const next = queue.current.shift();
    if (!next) return;
    const prev = shownRef.current;
    if (prev) pending.current = { before: capture(root.current), prev, skipped: (next as Shown & { skipped?: boolean }).skipped ?? false };
    shownRef.current = next;
    busy.current = true;
    setShown(next);
  }, [root]);

  useEffect(() => {
    if (!state.observation) return;
    const item: Shown = { observation: state.observation, log: state.log, revision: state.revision };
    if (!shownRef.current) { shownRef.current = item; setShown(item); return; }
    if (shownRef.current.observation === state.observation) return;
    const { queue: q, skipped, prioritized } = enqueueObservation(queue.current, item, state.observation, opts.current.self);
    // A position the viewer must act on is never left waiting behind animations: skip straight to it.
    if (prioritized) {
      queue.current = [{ ...item, skipped: skipped || busy.current } as Shown];
      if (timer.current) clearTimeout(timer.current);
      busy.current = false;
      pump();
      return;
    }
    queue.current = skipped ? [{ ...item, skipped: true } as Shown] : q;
    pump();
  }, [state.observation, state.log, state.revision, pump]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { clearActivity(root.current); for (const canvas of root.current?.querySelectorAll('canvas[data-board-renderer="pixi"]') ?? []) canvas.dispatchEvent(new CustomEvent(BOARD_MOTION, { detail: null })); }, [state.connection, options.paused, options.pace, root]);
  // Leaving replay resumes the queue (the latest live position, possibly skipped to).
  useEffect(() => { if (!options.paused) pump(); }, [options.paused, pump]);

  useLayoutEffect(() => {
    const p = pending.current;
    pending.current = null;
    if (!shown || !p) { busy.current = false; return; }
    const o = opts.current;
    const prevTable = buildTable(p.prev.observation, o.description, o.self);
    const nextTable = buildTable(shown.observation, o.description, o.self);
    const events: MotionEvent[] = diff(prevTable, nextTable);
    const own = !!o.self && (p.prev.observation.decisionOwners?.includes(o.self) ?? p.prev.observation.actor === o.self);
    const reduced = reducedMotion() || p.skipped;
    const planned = boundMotion(plan(events, { own, pace: o.pace, speed: o.speed, reducedMotion: reduced, segments: o.segments }),o.maximumMotionMs);
    const boardMotion: DisplayedMotion = { plan: planned, previous: prevTable, next: nextTable, revision: shown.revision, reset: p.skipped || shown.revision !== p.prev.revision + 1 || !!o.paused || state.connection !== 'open' };
    if (root.current) publishActivity(root.current, boardMotion);
    for (const canvas of root.current?.querySelectorAll('canvas[data-board-renderer="pixi"]') ?? []) canvas.dispatchEvent(new CustomEvent(BOARD_MOTION, { detail: boardMotion }));
    const claiming = root.current && layer.current && planned.mode !== 'none' ? execute(planned, root.current, layer.current, p.before) : new Map<string, number>();
    if (claiming.size) {
      setFlags({ claiming });
      setTimeout(() => setFlags(f => (f.claiming === claiming ? { claiming: new Map() } : f)), planned.total + 50);
    }
    // Opponent turns get a banner from the projected event log, so every command is legible.
    if (!own && o.pace !== 0 && events.length) {
      const known = new Set(p.prev.log.map(l => l.id));
      const lines = shown.log.filter(l => !known.has(l.id)).map(l => (o.formatLog ? o.formatLog(l.message) : l.message)).filter((l): l is string => !!l);
      if (lines.length) setBanner({ text: lines.slice(-2).join(' · '), color: o.seatColor(p.prev.observation.actor), key: shown.revision });
    }
    const wait = own || planned.mode === 'none' ? 0 : planned.total + 60;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      busy.current = false;
      pump();
    }, wait);
  }, [shown, root, layer, pump]);

  // Banners fade after a moment.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(b => (b?.key === banner.key ? null : b)), 2400);
    return () => clearTimeout(t);
  }, [banner]);

  return { shown, flags, banner };
}
